import React, { useEffect, useState, useCallback, useRef } from 'react';
import { cldThumb } from '../utils/img';
import {
  getBrands, getSales, listSnapshots, createSnapshot, deleteSnapshot,
  refreshOldInventory, refreshNewCatalog,
} from '../api/tempsor';

// Default "from" date — start of settlement era. No longer a hard floor; pick any earlier date to view older sales.
const FLOOR_DATE = '2026-04-22';

function buildBound(kind, value) {
  if (kind === 'now') return 'now';
  if (kind === 'snapshot') return value ? `snap:${value}` : null;
  return value || null;
}

export default function TempSor() {
  const [brands, setBrands] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [fromKind, setFromKind] = useState('date');
  const [fromValue, setFromValue] = useState(FLOOR_DATE);
  const [toKind, setToKind] = useState('now');
  const [toValue, setToValue] = useState('');

  const [showImages, setShowImages] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [savingSnap, setSavingSnap] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);

  // Escape closes the image modal
  useEffect(() => {
    if (!expandedImage) return;
    const handler = (e) => { if (e.key === 'Escape') setExpandedImage(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [expandedImage]);

  const reloadBrands = useCallback(async () => {
    const b = await getBrands();
    setBrands(b);
    setSelectedBrands(prev => prev.length === 0 ? b : prev.filter(x => b.includes(x)));
  }, []);

  const reloadSnapshots = useCallback(async () => {
    setSnapshots(await listSnapshots());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([reloadBrands(), reloadSnapshots()]);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load brands or time-logs.');
      }
    })();
  }, [reloadBrands, reloadSnapshots]);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const fromAt = buildBound(fromKind, fromValue);
      const toAt   = buildBound(toKind, toValue);
      const result = await getSales({ fromAt, toAt, brands: selectedBrands });
      setData(result);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load sales.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    if (!window.confirm('Refresh both inventories from Google Sheets?')) return;
    setRefreshing(true);
    try {
      const a = await refreshOldInventory();
      const b = await refreshNewCatalog();
      alert(`Refreshed.\nLegacy inventory: ${a.synced} rows.\nNew catalog: ${b.synced} rows.`);
      await reloadBrands();
    } catch (e) {
      alert(e.response?.data?.detail || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSaveSnapshot() {
    const name = window.prompt('Name this time-log (e.g. "April 2026 settlement"):');
    if (!name || !name.trim()) return;
    setSavingSnap(true);
    try {
      await createSnapshot(name.trim());
      await reloadSnapshots();
      alert('Time-log saved.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save time-log.');
    } finally {
      setSavingSnap(false);
    }
  }

  async function handleDeleteSnapshot(id) {
    if (!window.confirm('Delete this time-log? Reports already settled won\'t be affected.')) return;
    try {
      await deleteSnapshot(id);
      await reloadSnapshots();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete time-log.');
    }
  }


  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Temporary SOR view</h1>
          <p className="text-xs text-gray-500">Sales grouped by brand. Defaults to {FLOOR_DATE} onwards — change the "from" date to view older sales.</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={handleRefresh} disabled={refreshing}
                  className="text-sm bg-zoddle-blue text-white px-3 py-2 rounded hover:opacity-90 disabled:opacity-50">
            {refreshing ? 'Refreshing…' : 'Refresh inventories'}
          </button>
          <button onClick={handleSaveSnapshot} disabled={savingSnap}
                  className="text-sm bg-zoddle-green text-white px-3 py-2 rounded hover:opacity-90 disabled:opacity-50">
            {savingSnap ? 'Saving…' : 'Save time-log'}
          </button>
          <button onClick={() => window.print()}
                  className="text-sm bg-gray-700 text-white px-3 py-2 rounded hover:opacity-90">
            Print
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <BoundEditor label="From" kind={fromKind} setKind={setFromKind}
                       value={fromValue} setValue={setFromValue}
                       snapshots={snapshots} onDeleteSnapshot={handleDeleteSnapshot}
                       isFrom={true} />
          <BoundEditor label="To" kind={toKind} setKind={setToKind}
                       value={toValue} setValue={setToValue}
                       snapshots={snapshots} onDeleteSnapshot={handleDeleteSnapshot}
                       isFrom={false} />
          <BrandMultiSelect brands={brands} selected={selectedBrands}
                            setSelected={setSelectedBrands} />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center text-sm text-gray-700">
            <input type="checkbox" checked={showImages}
                   onChange={e => setShowImages(e.target.checked)} className="mr-2" />
            Show images (brand-wise view)
          </label>
          <button onClick={loadData} disabled={loading || selectedBrands.length === 0}
                  className="bg-zoddle-green text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50">
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-zoddle-pink text-sm mb-3 border border-zoddle-pink/30 rounded p-2 bg-zoddle-pink/5">
          {error}
        </div>
      )}

      {data && <SalesView data={data} showImages={showImages}
                          onImageClick={setExpandedImage} />}

      {expandedImage && (
        <div onClick={() => setExpandedImage(null)}
             className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out print:hidden">
          <img src={expandedImage} alt="" onClick={e => e.stopPropagation()}
               className="max-w-full max-h-full object-contain cursor-default" />
          <button onClick={() => setExpandedImage(null)}
                  className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-2xl leading-none"
                  aria-label="Close image preview">×</button>
        </div>
      )}
    </div>
  );
}

function BoundEditor({ label, kind, setKind, value, setValue, snapshots, onDeleteSnapshot, isFrom }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <div className="flex items-center gap-2 mb-1">
        <select value={kind} onChange={e => setKind(e.target.value)}
                className="text-xs border border-gray-300 rounded p-1">
          <option value="date">Date</option>
          <option value="snapshot">Time-log</option>
          <option value="now">Now</option>
        </select>
      </div>
      {kind === 'date' && (
        <input type="date" value={value}
               onChange={e => setValue(e.target.value)}
               className="w-full border border-gray-300 rounded p-1 text-sm" />
      )}
      {kind === 'snapshot' && (
        <div>
          <select value={value} onChange={e => setValue(e.target.value)}
                  className="w-full border border-gray-300 rounded p-1 text-sm">
            <option value="">— Select time-log —</option>
            {snapshots.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.snapshot_date_ist})</option>
            ))}
          </select>
          {value && (
            <button onClick={() => { onDeleteSnapshot(value); setValue(''); }}
                    className="text-xs text-zoddle-pink hover:underline mt-1">
              Delete this time-log
            </button>
          )}
          {isFrom && value && (
            <div className="text-xs text-gray-500 italic mt-1">
              Sales after the time-log's date (exclusive).
            </div>
          )}
        </div>
      )}
      {kind === 'now' && (
        <div className="text-xs text-gray-500 italic">Resolves to today at request time.</div>
      )}
    </div>
  );
}

function SalesView({ data, showImages, onImageClick }) {
  return (
    <div>
      <div className="bg-zoddle-blue/10 border border-zoddle-blue/30 rounded p-3 mb-3 text-sm text-gray-700 print:bg-white print:border-gray-300">
        <div>
          Sales from <strong>{data.from_at}</strong>{' '}
          {data.from_kind === 'exclusive' ? '(exclusive)' : '(inclusive)'} to{' '}
          <strong>{data.to_at}</strong> (inclusive).
        </div>
        {data.totals_overall && (
          <div className="text-xs text-gray-500 mt-1">
            {data.totals_overall.brands_count} brand(s) ·{' '}
            {data.totals_overall.matched_rows} matched sale row(s) ·{' '}
            {data.totals_overall.unmatched_rows} unmatched
          </div>
        )}
      </div>

      {data.brands.length === 0 && (
        <div className="text-gray-500 text-sm p-6 text-center bg-white border border-gray-200 rounded">
          No matched sales for the selected filters.
        </div>
      )}

      {data.brands.map(b => (
        <BrandSection key={b.brand} data={b} showImages={showImages}
                      onImageClick={onImageClick} />
      ))}

      {data.unmatched.length > 0 && <UnmatchedSection rows={data.unmatched} />}
      {data.override_issues && data.override_issues.length > 0 && (
        <OverrideIssuesSection rows={data.override_issues} />
      )}
    </div>
  );
}

function OverrideIssuesSection({ rows }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-4 break-inside-avoid">
      <button onClick={() => setExpanded(e => !e)}
              className="w-full text-left flex items-center justify-between text-yellow-800 hover:opacity-80 print:cursor-default">
        <span className="font-bold text-sm">⚠ Re-sticker overrides with missing legacy match ({rows.length})</span>
        <span className="print:hidden text-xs">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      <div className="text-xs text-gray-600 mt-1">
        These sales have a re-sticker override pointing at a legacy SKU ID that isn't in
        the old inventory sheet. Fix the override or the legacy sheet, then refresh inventories.
      </div>
      {expanded && (
        <table className="w-full text-xs mt-2 border-collapse">
          <thead className="bg-yellow-100 print:bg-white">
            <tr>
              <th className="border border-yellow-300 p-1 text-left">Scanned barcode</th>
              <th className="border border-yellow-300 p-1 text-left">Points at SKU</th>
              <th className="border border-yellow-300 p-1 text-right">Qty</th>
              <th className="border border-yellow-300 p-1 text-left">Sale date</th>
              <th className="border border-yellow-300 p-1 text-left">Order ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="border border-yellow-300 p-1 font-mono">{r.scanned_barcode}</td>
                <td className="border border-yellow-300 p-1 font-mono">{r.override_target_sku_id}</td>
                <td className="border border-yellow-300 p-1 text-right">{r.qty}</td>
                <td className="border border-yellow-300 p-1">{r.sale_date}</td>
                <td className="border border-yellow-300 p-1">{r.order_id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BrandSection({ data, showImages, onImageClick }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="bg-white border border-gray-200 rounded mb-4 break-inside-avoid">
      <button onClick={() => setExpanded(e => !e)}
              className="w-full text-left p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 print:bg-white print:cursor-default">
        <div className="min-w-0 flex-1">
          <span className="font-bold text-lg">{data.brand}</span>
          <div className="text-sm text-gray-600 mt-1">
            {data.totals.qty} units · ₹{data.totals.amount_payable_excl_tax} (excl tax) · ₹{data.totals.amount_payable_incl_tax} (incl tax)
            {data.totals.rows_with_no_cost > 0 && (
              <span className="text-zoddle-pink ml-2">
                ⚠ {data.totals.rows_with_no_cost} row(s) missing cost
              </span>
            )}
          </div>
        </div>
        <span className="print:hidden text-gray-500 ml-2">{expanded ? '▼' : '▶'}</span>
      </button>
      {expanded && (
        <div className="p-3">
          {data.old.length > 0 && (
            <SalesTable title="Old inventory" rows={data.old}
                        showImages={showImages} legacyTaxNote={true}
                        onImageClick={onImageClick} />
          )}
          {data.new.length > 0 && (
            <SalesTable title="New inventory" rows={data.new}
                        showImages={showImages} legacyTaxNote={false}
                        onImageClick={onImageClick} />
          )}
        </div>
      )}
    </div>
  );
}

function SalesTable({ title, rows, showImages, legacyTaxNote, onImageClick }) {
  const hasRestickered = rows.some(r => r.restickered);
  return (
    <div className="mb-4">
      <h3 className="text-sm font-bold text-gray-700 mb-2">
        {title}
        {legacyTaxNote && (
          <span className="text-xs text-gray-500 ml-2 font-normal">
            (Payable incl tax assumes 5% on cost)
          </span>
        )}
      </h3>
      <table className="w-full text-xs border-collapse">
        <thead className="bg-gray-50 print:bg-white">
          <tr>
            {showImages && <th className="border border-gray-200 p-1 w-16">Image</th>}
            <th className="border border-gray-200 p-1 text-left">SKU / Barcode</th>
            <th className="border border-gray-200 p-1 text-left">Design</th>
            <th className="border border-gray-200 p-1 text-left">Size</th>
            <th className="border border-gray-200 p-1 text-right">Qty</th>
            <th className="border border-gray-200 p-1 text-left">Sale date</th>
            <th className="border border-gray-200 p-1 text-right">Cost</th>
            <th className="border border-gray-200 p-1 text-right">Payable (excl)</th>
            <th className="border border-gray-200 p-1 text-right">Payable (incl)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {showImages && (
                <td className="border border-gray-200 p-1 text-center">
                  {r.image_url
                    ? <img src={cldThumb(r.image_url, 300)} alt="" loading="lazy"
                           onClick={() => onImageClick && onImageClick(r.image_url)}
                           className="w-12 h-12 object-cover inline-block cursor-zoom-in hover:opacity-75 transition-opacity print:cursor-default" />
                    : <span className="text-gray-300">—</span>}
                </td>
              )}
              <td className="border border-gray-200 p-1 font-mono">
                {r.sku_id || r.zoddle_barcode}{r.restickered ? '*' : ''}
              </td>
              <td className="border border-gray-200 p-1">{r.design_name || '—'}</td>
              <td className="border border-gray-200 p-1">{r.size || '—'}</td>
              <td className="border border-gray-200 p-1 text-right">{r.qty}</td>
              <td className="border border-gray-200 p-1">{r.sale_date}</td>
              <td className="border border-gray-200 p-1 text-right">
                {r.cost_price ? `₹${r.cost_price}` : '—'}
              </td>
              <td className="border border-gray-200 p-1 text-right">
                {r.amount_payable_excl_tax ? `₹${r.amount_payable_excl_tax}` : '—'}
              </td>
              <td className="border border-gray-200 p-1 text-right">
                {r.amount_payable_incl_tax ? `₹${r.amount_payable_incl_tax}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasRestickered && (
        <div className="text-xs text-gray-500 mt-1 italic">
          * Re-stickered with a new Zoddle barcode; legacy SKU ID shown.
        </div>
      )}
    </div>
  );
}

function BrandMultiSelect({ brands, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on click-outside
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggle(b) {
    setSelected(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);
  }

  const q = search.trim().toLowerCase();
  const filtered = q ? brands.filter(b => b.toLowerCase().includes(q)) : brands;

  let summary;
  if (selected.length === 0) summary = 'No brands selected';
  else if (selected.length === brands.length) summary = `All ${brands.length} brands`;
  else if (selected.length <= 3) summary = selected.join(', ');
  else summary = `${selected.length} of ${brands.length} brands`;

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-gray-600">Brands</label>
        <div className="flex gap-2 text-xs">
          <button type="button" onClick={() => setSelected(brands)}
                  className="text-zoddle-blue hover:underline">all</button>
          <button type="button" onClick={() => setSelected([])}
                  className="text-zoddle-blue hover:underline">clear</button>
        </div>
      </div>
      <button type="button" onClick={() => setOpen(o => !o)}
              className="w-full border border-gray-300 rounded p-2 text-sm text-left bg-white hover:bg-gray-50 flex items-center justify-between">
        <span className="truncate">{summary}</span>
        <span className="text-gray-400 ml-2">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-72 overflow-hidden flex flex-col">
          <input type="text" placeholder="Search brands…" value={search}
                 onChange={e => setSearch(e.target.value)}
                 className="w-full px-2 py-1 border-b border-gray-200 text-sm focus:outline-none" />
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="text-xs text-gray-500 p-2">No matches.</div>
            ) : (
              filtered.map(b => (
                <label key={b}
                       className="flex items-center px-2 py-1 hover:bg-gray-100 cursor-pointer text-sm">
                  <input type="checkbox" checked={selected.includes(b)}
                         onChange={() => toggle(b)} className="mr-2" />
                  <span className="truncate">{b}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UnmatchedSection({ rows }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-zoddle-pink/5 border border-zoddle-pink/30 rounded p-3 mt-4 break-inside-avoid">
      <button onClick={() => setExpanded(e => !e)}
              className="w-full text-left flex items-center justify-between text-zoddle-pink hover:opacity-80 print:cursor-default">
        <span className="font-bold text-sm">⚠ Unmatched ({rows.length})</span>
        <span className="print:hidden text-xs">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      <div className="text-xs text-gray-600 mt-1">
        Sale rows whose SKU ID didn't match either inventory. Update the catalogs and click "Refresh inventories".
      </div>
      {expanded && (
        <table className="w-full text-xs mt-2 border-collapse">
          <thead className="bg-zoddle-pink/10 print:bg-white">
            <tr>
              <th className="border border-zoddle-pink/30 p-1 text-left">SKU / Barcode</th>
              <th className="border border-zoddle-pink/30 p-1 text-right">Qty</th>
              <th className="border border-zoddle-pink/30 p-1 text-left">Sale date</th>
              <th className="border border-zoddle-pink/30 p-1 text-left">Order ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="border border-zoddle-pink/30 p-1 font-mono">{r.sku_id_or_barcode}</td>
                <td className="border border-zoddle-pink/30 p-1 text-right">{r.qty}</td>
                <td className="border border-zoddle-pink/30 p-1">{r.sale_date}</td>
                <td className="border border-zoddle-pink/30 p-1">{r.order_id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
